import React, { type ReactNode, useMemo } from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { resolvePrimaryNavigationMode } from "./layout/primaryNavigationLayout";
import ChatSection from "./ChatSection";
import AnnotationsList from "./AnnotationsList";
import { useLayout } from "../context/LayoutContext";
import { ConnectorMarketplaceHeaderProvider } from "../context/ConnectorMarketplaceHeaderContext";
import { KgHeaderProvider } from "../context/KgHeaderContext";
import { StoryHeaderProvider } from "../context/StoryHeaderContext";
import { useGlobalPath } from "../hooks/useGlobalPath";
import { Shell, resolveLayout } from "./layout";
import {
  type MainContentBehavior,
  resolveMainContentBehavior,
} from "./layout/mainContentLayout";
// Side-effect import: registers all route → layout mappings exactly once.
import "./layout/registerLayouts";

/** Login (and similar bare routes) — gradient background, no shell grid. */
const BareWrapper = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  background: linear-gradient(
    to right,
    ${({ theme }) => theme.colors.background.secondary},
    ${({ theme }) => theme.colors.background.tertiary}
  );
  box-sizing: border-box;
`;

/**
 * Inner main wrapper. Sits inside Shell's `<main>` slot. Owns the dynamic
 * route chrome and the dynamic chat / present / annotation margins. Routes
 * without registry chrome retain their legacy wrapper behavior.
 */
const MainContentInner = styled.div<{
  $isChatOpen: boolean;
  $isPresentMode: boolean;
  $isAnnotationsListOpen: boolean;
  $layout: MainContentBehavior;
  $isPhone: boolean;
}>`
  height: 100%;
  padding: ${({ $layout, theme }) =>
    $layout.hasGutter
      ? `calc(${theme.spacing.base[2]} + ${theme.spacing.base[0.5]})`
      : "0"};
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;
  overflow-y: ${({ $layout }) => $layout.overflowY};
  transition:
    margin-right ${({ theme }) => theme.transitions.durations.normal}
      ${({ theme }) => theme.transitions.easing.easeInOut},
    margin-left ${({ theme }) => theme.transitions.durations.normal}
      ${({ theme }) => theme.transitions.easing.easeInOut};
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  margin-right: ${({
    $isAnnotationsListOpen,
    $isChatOpen,
    $isPresentMode,
    $layout,
    $isPhone,
  }) => {
    if ($isPhone) return "0%";
    if ($isAnnotationsListOpen) return "0%";
    if ($isChatOpen) return "25%";
    if ($isPresentMode || $layout.isFullWidth) return "0%";
    return "12.5%";
  }};
  margin-left: ${({
    $isAnnotationsListOpen,
    $isChatOpen,
    $isPresentMode,
    $layout,
    $isPhone,
  }) => {
    if ($isPhone) return "0%";
    if ($isAnnotationsListOpen) return "25%";
    if ($isChatOpen || $layout.isFullWidth) return "0%";
    if ($isPresentMode) return "25%";
    return "12.5%";
  }};
`;

const MemoizedMainContentInner = React.memo(MainContentInner);

interface ResponsiveLayoutProps {
  children: ReactNode;
  handleLogout: () => void;
}

const ResponsiveLayout = React.memo<ResponsiveLayoutProps>(
  ({ children, handleLogout }) => {
    const {
      sidebarVisible,
      isChatOpen,
      isPresentMode,
      isAnnotationsListOpen,
      navDrawerOpen,
      navDrawerState,
      navPinAvailable,
      viewport = "desktop",
      enterNavTrigger,
      leaveNavTrigger,
      enterNavPanel,
      leaveNavPanel,
      toggleSidebar,
      toggleNavDrawer,
      closeNavDrawer,
      toggleChat,
      toggleAnnotationsList,
      setTab,
    } = useLayout();

    const {
      isGlobalPath,
      isLoginPath,
      isHomePath,
      isConnectorDetailPath,
      isKnowledgeGraphPath,
      isWorkspacesPath,
      isStoryDetailPath,
    } = useGlobalPath();
    const location = useLocation();
    const isFluidicDashboard = location.pathname.includes("/explore");
    const isBuildMode =
      location.pathname.includes("/build/") || isFluidicDashboard;
    const isInteractMode = location.pathname.includes("/interact");
    const resolvedLayout = resolveLayout(location.pathname);

    const isPhone = viewport === "phone";
    const isDrawerNavigation =
      resolvePrimaryNavigationMode(
        resolvedLayout.chrome?.primaryNavigation,
        { isGlobalPath, isConnectorDetailPath, isKnowledgeGraphPath },
        isPhone,
      ) === "drawer";

    const memoizedHeaderProps = useMemo(
      () => ({
        // The header's menu button opens the drawer on routes that use one.
        toggleSidebar: isDrawerNavigation ? toggleNavDrawer : toggleSidebar,
        toggleChat,
        setTab,
        onLogout: handleLogout,
        pageTitle: resolvedLayout.chrome?.headerTitle,
        menuOpen: isDrawerNavigation ? navDrawerOpen : sidebarVisible,
        menuPinned: isDrawerNavigation ? navDrawerState === "pinned" : undefined,
        onMenuPointerEnter: isDrawerNavigation ? enterNavTrigger : undefined,
        onMenuPointerLeave: isDrawerNavigation ? leaveNavTrigger : undefined,
      }),
      [
        isDrawerNavigation,
        navDrawerOpen,
        navDrawerState,
        enterNavTrigger,
        leaveNavTrigger,
        sidebarVisible,
        toggleNavDrawer,
        toggleSidebar,
        toggleChat,
        setTab,
        handleLogout,
        resolvedLayout.chrome?.headerTitle,
      ],
    );

    if (isLoginPath) {
      return <BareWrapper>{children}</BareWrapper>;
    }

    // Slot components for the current route — driven by layoutRegistry.
    // Add a new route's layout by updating registerLayouts.ts.
    const { Breadcrumb, Secondary, secondaryWidth, chrome } = resolvedLayout;
    // On a phone every route that shows navigation uses the drawer, whatever its registered mode.
    const primaryNavigationMode =
      isPhone && chrome?.primaryNavigation !== "hidden" ? "drawer" : chrome?.primaryNavigation;
    const shouldRenderPrimaryNavigation =
      (sidebarVisible || isDrawerNavigation) &&
      chrome?.primaryNavigation !== "hidden";
    const mainContentBehavior = resolveMainContentBehavior(
      chrome?.mainContent,
      {
        isGlobalPath,
        isFluidicDashboard,
        isBuildMode,
        isInteractMode,
        isConnectorDetailPath,
        isKnowledgeGraphPath,
        isWorkspacesPath,
        isHomePath,
        isStoryDetailPath,
      },
    );

    return (
      <ConnectorMarketplaceHeaderProvider>
        <KgHeaderProvider>
          <StoryHeaderProvider>
            <Shell
              header={<Header {...memoizedHeaderProps} />}
              sidebar={
                // Story pages use the design's hamburger plus overlay drawer, with no
                // permanent rail (Hardik's decision, visual fix round VB-1; registerLayouts).
                shouldRenderPrimaryNavigation ? (
                  <Sidebar
                    toggleAnnotationsList={toggleAnnotationsList}
                    primaryNavigation={primaryNavigationMode}
                    drawerOpen={navDrawerOpen}
                    onDrawerClose={closeNavDrawer}
                    drawerVariant={navPinAvailable ? "peek" : "modal"}
                    drawerDocked={navDrawerState === "pinned"}
                    onDrawerPinToggle={navPinAvailable ? toggleNavDrawer : undefined}
                    onDrawerPointerEnter={enterNavPanel}
                    onDrawerPointerLeave={leaveNavPanel}
                  />
                ) : null
              }
              breadcrumb={Breadcrumb ? <Breadcrumb /> : null}
              secondary={Secondary ? <Secondary /> : null}
              secondaryWidth={secondaryWidth}
              sidebarOverlay={isDrawerNavigation}
              sidebarPinned={isDrawerNavigation && navDrawerState === "pinned"}
              viewport={viewport}
              main={
                <MemoizedMainContentInner
                  $isChatOpen={isChatOpen}
                  $isPresentMode={isPresentMode}
                  $isAnnotationsListOpen={isAnnotationsListOpen}
                  $layout={mainContentBehavior}
                  $isPhone={isPhone}
                  data-main-content-mode={chrome?.mainContent ?? "legacy"}
                  data-testid="main-content-inner"
                >
                  {children}
                </MemoizedMainContentInner>
              }
              overlays={
                <>
                  {(isBuildMode || isFluidicDashboard) && (
                    <AnnotationsList
                      isOpen={isAnnotationsListOpen}
                      onClose={toggleAnnotationsList}
                      isBuildMode={isBuildMode}
                    />
                  )}
                  <ChatSection isChatOpen={isChatOpen} onClose={toggleChat} />
                </>
              }
            />
          </StoryHeaderProvider>
        </KgHeaderProvider>
      </ConnectorMarketplaceHeaderProvider>
    );
  }
);

export default ResponsiveLayout;
